import type { Polygon } from 'geojson';

import type { FloodJob, FloodJobStep, SarAoiAnalysisKind } from '@/mocks/floodJobs';
import {
  DEFAULT_STAGE_WEIGHTS,
  DEFAULT_TARGET_DURATION_MS,
  buildMockSarSceneGalleryFromParameters,
} from '@/mocks/floodJobs';

function pickStr(row: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = row[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return '';
}

function pickNum(row: Record<string, unknown>, keys: string[]): number | undefined {
  for (const k of keys) {
    const v = row[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim()) {
      const n = Number(v);
      if (!Number.isNaN(n)) return n;
    }
  }
  return undefined;
}

function fallbackJobId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `geo-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function mapGeoStatus(raw: string): FloodJob['status'] {
  const s = raw.toLowerCase();
  if (/completed|succeeded|success|done/.test(s)) return 'completed';
  if (/failed|error/.test(s)) return 'failed';
  if (/running|processing|active|in_progress|working/.test(s)) return 'running';
  if (/cancel|canceled|cancelled/.test(s)) return 'failed';
  return 'queued';
}

function defaultSteps(status: FloodJob['status']): FloodJobStep[] {
  const labels = ['Collect', 'Preprocess', 'Inference', 'Aggregate', 'Export'];
  return labels.map((name, i) => {
    let st: FloodJobStep['status'] = 'pending';
    if (status === 'completed') st = 'done';
    else if (status === 'failed') {
      if (i === 0) st = 'done';
      else if (i === 1) st = 'error';
    } else if (status === 'running') {
      if (i < 2) st = 'done';
      else if (i === 2) st = 'running';
    }
    return { name, status: st };
  });
}

/**
 * Ánh xạ một dòng job từ Geo-processing / gateway (`GET /api/flood-jobs`) sang `FloodJob` UI.
 * Contract BE có thể khác — chỉ đọc field phổ biến (snake_case + camelCase).
 */
export function mapGeoJobRowToFloodJob(row: Record<string, unknown>): FloodJob {
  // Nếu có metadata (từ API kết quả), ta merge nó vào row để các hàm pickStr bên dưới tìm thấy param
  if (row.metadata && typeof row.metadata === 'object' && !Array.isArray(row.metadata)) {
    row = { ...row, ...(row.metadata as any) };
  }
  const id = pickStr(row, ['job_id', 'id', 'uuid', 'external_id']) || fallbackJobId();
  const statusRaw = pickStr(row, ['status', 'job_status', 'state']);
  let status = mapGeoStatus(statusRaw || 'queued');
  // Nếu là object kết quả chi tiết (có metadata/geometry) nhưng thiếu status -> mặc định là completed
  if (!statusRaw && (row.metadata || row.geometry)) {
    status = 'completed';
  }
  const progressPct =
    pickNum(row, [
      'progress_percent',
      'progress_pct',
      'progress',
      'completion_pct',
      'percent_complete',
    ]) ?? (status === 'completed' ? 100 : 0);
  const progress = Math.min(100, Math.max(0, Math.round(progressPct)));

  const startedAt =
    pickStr(row, ['started_at', 'created_at', 'create_date', 'submitted_at', 'queued_at']) ||
    new Date().toISOString();
  const finishedAt =
    pickStr(row, ['finished_at', 'completed_at', 'write_date', 'ended_at']) || undefined;
  const name =
    pickStr(row, ['job_name', 'name', 'title', 'display_name']) ||
    pickStr(row, ['task_type', 'taskType']) ||
    id;
  const submittedBy = pickStr(row, ['submitted_by', 'created_by', 'owner', 'user']) || '—';
  const errorMessage =
    pickStr(row, ['error_message', 'errorMessage', 'failure_reason', 'failureReason', 'detail']) ||
    undefined;

  const requestPayload =
    (row.request_payload as Record<string, unknown>) || (row as Record<string, unknown>);

  const aoi =
    pickStr(row, ['aoi_label', 'region_name', 'basin_name', 'description', 'notes']) ||
    pickStr(requestPayload, ['region_name', 'aoi_label']) ||
    '—';
  const resultId = pickStr(row, ['result_id', 'resultId']);

  const parameters = (row.parameters as Record<string, unknown>) || {};
  const timeWindow = (requestPayload.time_window as Record<string, unknown>) || {};

  const baseline =
    pickStr(row, ['baseline_start', 'baseline', 'baseline_period']) ||
    pickStr(timeWindow, ['baseline_start', 'baseline_start_date']) ||
    pickStr(parameters, ['baseline_start', 'baseline', 'baseline_period']) ||
    startedAt.slice(0, 10);

  const crisis =
    pickStr(row, ['crisis_start', 'crisis', 'crisis_period']) ||
    pickStr(timeWindow, ['crisis_start', 'crisis_start_date']) ||
    pickStr(parameters, ['crisis_start', 'crisis', 'crisis_period']) ||
    startedAt.slice(0, 10);

  const polarization =
    pickStr(row, ['polarization', 'pol']) || pickStr(parameters, ['polarization', 'pol']) || 'VV';

  const threshold =
    pickNum(row, ['water_threshold_db', 'threshold_db', 'threshold']) ??
    pickNum(parameters, ['water_threshold_db', 'threshold_db', 'threshold']) ??
    -16;

  const metadata = (row.metadata as Record<string, unknown>) || {};
  const kindRaw = (
    pickStr(row, ['analysis_kind', 'analysisKind', 'task_type']) ||
    pickStr(parameters, ['analysis_kind', 'analysisKind', 'task_type']) ||
    pickStr(metadata, ['analysis_kind', 'analysisKind', 'task_type'])
  ).toLowerCase();
  const analysisKind: SarAoiAnalysisKind =
    kindRaw.includes('land') || kindRaw.includes('veg') ? 'landCover' : 'sarFlood';

  const runtime = {
    targetDurationMs: DEFAULT_TARGET_DURATION_MS[analysisKind],
    stageWeights: DEFAULT_STAGE_WEIGHTS[analysisKind],
  };

  const resultPayload = (row.result_payload as Record<string, unknown>) || {};

  const aoiAreaKm2 =
    pickNum(row, ['aoi_area_km2', 'aoiAreaKm2', 'total_area_km2']) ??
    pickNum(resultPayload, ['total_area_km2']);

  let floodedKm2 =
    pickNum(row, ['flooded_area_km2', 'floodedAreaKm2', 'area_km2']) ??
    pickNum(resultPayload, ['flooded_area_km2']);
  if (status === 'completed' && floodedKm2 === undefined) {
    floodedKm2 = 0;
  }
  const rawAccuracy =
    pickNum(resultPayload, ['accuracy_score']) ??
    pickNum(row, ['accuracy_score', 'accuracyScore', 'score']);
  const accuracy =
    rawAccuracy != null && rawAccuracy <= 1 && rawAccuracy > 0
      ? Math.round(rawAccuracy * 100)
      : rawAccuracy;

  const floodExtentGeometry =
    row.flood_extent_geojson ?? resultPayload.flood_extent_geojson ?? row.geometry;

  const baselineEnd =
    pickStr(timeWindow, ['baseline_end']) || pickStr(row, ['baseline_end']) || baseline;
  const crisisEnd = pickStr(timeWindow, ['crisis_end']) || pickStr(row, ['crisis_end']) || crisis;

  // Extract AOI Polygon from request_payload
  let aoiPolygon: Polygon | undefined;
  const rawAoi = (requestPayload.aoi || requestPayload.aoi_geometry) as any;
  if (rawAoi) {
    if (rawAoi.type === 'FeatureCollection' && rawAoi.features?.[0]?.geometry) {
      aoiPolygon = rawAoi.features[0].geometry;
    } else if (rawAoi.type === 'Feature' && rawAoi.geometry) {
      aoiPolygon = rawAoi.geometry;
    } else if (rawAoi.type === 'Polygon' || rawAoi.type === 'MultiPolygon') {
      aoiPolygon = rawAoi;
    }
  }

  // Real dates from BE if available
  const apiRefDates = resultPayload.reference_dates as string[] | undefined;
  let sceneGallery;
  if (status === 'completed') {
    if (Array.isArray(apiRefDates) && apiRefDates.length > 0) {
      // Build skeleton from real BE dates
      sceneGallery = apiRefDates.map((date) => {
        // Simple heuristic: earlier dates are baseline, later are crisis
        const isBaseline = date <= baselineEnd;
        return {
          role: isBaseline ? ('baseline' as const) : ('crisis' as const),
          referenceDate: date,
          periodLabel: date, // UI will format this
        };
      });
    } else {
      // Fallback to skeleton from parameters if BE didn't return explicit dates
      sceneGallery = buildMockSarSceneGalleryFromParameters({
        baseline,
        crisis,
        polarization,
        threshold,
        analysisKind,
      });
    }
  }

  const vegetationCoverPct =
    pickNum(row, ['vegetation_cover_pct', 'veg_pct', 'veg_cover_pct', 'vegetationCoverPct']) ??
    pickNum(resultPayload, ['vegetation_cover_pct', 'veg_pct', 'veg_cover_pct']);

  const affectedPop =
    pickNum(row, ['affected_pop', 'affectedPop', 'population']) ??
    pickNum(resultPayload, ['affected_pop', 'population']);

  const results =
    status === 'completed' &&
    (floodedKm2 != null ||
      accuracy != null ||
      floodExtentGeometry != null ||
      vegetationCoverPct != null)
      ? {
          floodedAreaKm2: floodedKm2 ?? 0,
          accuracyScore: accuracy ?? 0,
          affectedPop: affectedPop ?? 0,
          vegetationCoverPct,
          depthAtPointCm:
            pickNum(row, ['depth_at_point_cm', 'depthAtPointCm', 'avg_depth_cm']) ??
            pickNum(resultPayload, ['depth_at_point_cm', 'avg_depth_cm']),
          estimatedDepthMinM:
            pickNum(row, ['estimated_depth_min_m', 'depth_min_m', 'estimatedDepthMinM']) ??
            pickNum(resultPayload, ['estimated_depth_min_m', 'depth_min_m']),
          estimatedDepthMaxM:
            pickNum(row, ['estimated_depth_max_m', 'depth_max_m', 'estimatedDepthMaxM']) ??
            pickNum(resultPayload, ['estimated_depth_max_m', 'depth_max_m']),
          affectedAreaPercent:
            pickNum(row, ['affected_area_percent', 'affected_pct', 'affectedAreaPercent']) ??
            pickNum(resultPayload, ['affected_area_percent', 'affected_pct']),
          landCoverImpacts: (row.land_cover_impacts ?? resultPayload.land_cover_impacts) as any,
          nearbyInfrastructure: (() => {
            const raw = row.nearby_infrastructure ?? resultPayload.nearby_infrastructure;
            if (!raw) return undefined;
            const r = raw as Record<string, any>;
            return {
              hospitals: r.hospitals ?? r.hospital ?? 0,
              schools: r.schools ?? r.school ?? 0,
              emergencyShelters: r.emergency_shelters ?? r.emergency_shelter ?? 0,
              fireStations: r.fire_stations ?? r.fire_station ?? 0,
              policeStations: r.police_stations ?? r.police_station ?? 0,
              evacuationCenters: r.evacuation_centers ?? r.evacuation_center ?? 0,
            };
          })(),
          blockedRoads: (row.blocked_roads ?? resultPayload.blocked_roads) as any,
          damageAssessment: (() => {
            const raw = row.damage_assessment ?? resultPayload.damage_assessment;
            if (!raw) return undefined;
            const r = raw as Record<string, any>;
            return {
              affectedHouses: r.affected_houses ?? r.affectedHouses ?? 0,
              damagedHouses: r.damaged_houses ?? r.damaged_houses ?? 0,
              destroyedHouses: r.destroyed_houses ?? r.destroyed_houses ?? 0,
              economicLossUSD: r.economic_loss_usd ?? r.economicLossUSD ?? 0,
              agriculturalAreaHa: r.agricultural_area_ha ?? r.agriculturalAreaHa ?? 0,
            };
          })(),
          floodedAreaHa: floodedKm2 != null ? Math.round(floodedKm2 * 100 * 100) / 100 : undefined,
          ...(floodExtentGeometry ? { floodExtentGeometry: floodExtentGeometry as any } : {}),
          ...(sceneGallery ? { sceneGallery } : {}),
        }
      : undefined;

  return {
    id,
    resultId,
    name,
    status,
    progress,
    aoi,
    aoiPolygon,
    aoiAreaKm2,
    startedAt,
    ...(finishedAt ? { finishedAt } : {}),
    ...(errorMessage ? { errorMessage } : {}),
    submittedBy,
    parameters: {
      baseline,
      crisis,
      baselineEnd,
      crisisEnd,
      polarization,
      threshold,
      analysisKind,
    },
    runtime,
    ...(results ? { results } : {}),
    steps: defaultSteps(status),
  };
}
